import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Download, Search, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AccountUploadButtons } from "../components/LedgerImportPreview";
import { BalanceMatch } from "../components/BalanceMatch";

const dateField = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";
const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-xs font-medium mb-1 text-muted-foreground";

export default function CreditPartyLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ date: today(), particulars: "", debit: "", credit: "" });

  const addEntry = async () => {
    const debit = parseFloat(form.debit) || 0;
    const credit = parseFloat(form.credit) || 0;
    if (!form.date) return toast.error("Pick a date");
    if (debit <= 0 && credit <= 0) return toast.error("Enter a debit or credit amount");
    try {
      await api.post("/ledger-entries", { scope: "party", target_id: id, date: form.date, particulars: form.particulars, debit, credit });
      toast.success("Entry added");
      setForm({ date: form.date, particulars: "", debit: "", credit: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delEntry = async (mid) => {
    if (!window.confirm("Delete this manual entry?")) return;
    try { await api.delete(`/ledger-entries/${mid}`); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    try {
      const params = { format: "json" };
      if (start) params.start = start;
      if (end) params.end = end;
      setData((await api.get(`/parties/${id}/ledger`, { params })).data);
    } catch { toast.error("Could not load ledger"); }
  }, [id, start, end]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.rows;
    return data.rows.filter((r) =>
      String(r.ref).toLowerCase().includes(term) || (r.particulars || "").toLowerCase().includes(term) || (r.date || "").includes(term));
  }, [data, q]);

  const download = async (format) => {
    try {
      const params = { format };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/parties/${id}/ledger`, { params, responseType: "blob" });
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ledger_${(data?.party?.name || "party").replace(/\s/g, "_")}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch { toast.error("Download failed"); }
  };

  const p = data?.party;

  const fmtBal = (v) => {
    const n = Math.round((v || 0) * 100) / 100;
    const tag = n > 0.009 ? "DR" : n < -0.009 ? "CR" : "";
    return `${inr(Math.abs(n))}${tag ? " " + tag : ""}`;
  };
  const closing = data?.rows?.length ? data.rows[data.rows.length - 1].balance : 0;

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <button data-testid="ledger-back" onClick={() => navigate("/credit")}
          className="mb-4 h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
          <ArrowLeft size={14} /> Back to Credit Parties
        </button>

        <PageHeader title={p?.name || "Ledger"} subtitle={`${p?.account_no ? "A/C " + p.account_no + " · " : ""}${p?.member_count > 1 ? `Combined account · ${p.member_count} parties` : "Credit ledger statement"}`}>
          <div className="flex flex-wrap gap-2">
            {canWrite && <AccountUploadButtons partyId={id} title={p?.name || "Account"} onDone={load} />}
            <BalanceMatch partyId={id} />
            <button data-testid="ledger-page-pdf" onClick={() => download("pdf")}
              className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Download size={15} /> PDF</button>
            <button data-testid="ledger-page-csv" onClick={() => download("csv")}
              className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><FileText size={15} /> Excel/CSV</button>
          </div>
        </PageHeader>

        {p && (p.guarantor_name || p.phone || p.vehicle_number) && (
          <div className="rounded-xl border border-border bg-card p-4 mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm animate-fade-up">
            <div><div className="text-xs text-muted-foreground">Phone</div><div className="num">{p.phone || "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Vehicle</div><div className="num">{p.vehicle_number || "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Guarantor</div><div>{p.guarantor_name || "—"}{p.guarantor_phone ? ` · ${p.guarantor_phone}` : ""}</div></div>
            <div><div className="text-xs text-muted-foreground">Guarantor A/c</div><div>{p.guarantor_account || "—"}</div></div>
          </div>
        )}

        {canWrite && (
          <div data-testid="credit-ledger-add" className="rounded-xl border border-border bg-card p-4 mb-5 animate-fade-up">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Plus size={15} /> Add general entry (history before your cash book starts — does not touch the cash book)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <div><label className={label}>Date</label>
                <input data-testid="credit-entry-date" type="date" className={field + " num"} value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} /></div>
              <div className="col-span-2 md:col-span-2"><label className={label}>Particulars</label>
                <input data-testid="credit-entry-particulars" className={field} placeholder="e.g. Opening goods, Payment received…" value={form.particulars} onChange={(ev) => setForm({ ...form, particulars: ev.target.value })} /></div>
              <div><label className={label}>Debit (dues)</label>
                <input data-testid="credit-entry-debit" type="number" step="0.01" min="0" className={field + " num"} placeholder="0.00" value={form.debit} onChange={(ev) => setForm({ ...form, debit: ev.target.value })} /></div>
              <div><label className={label}>Credit (paid)</label>
                <input data-testid="credit-entry-credit" type="number" step="0.01" min="0" className={field + " num"} placeholder="0.00" value={form.credit} onChange={(ev) => setForm({ ...form, credit: ev.target.value })} /></div>
            </div>
            <div className="mt-3 flex justify-end">
              <button data-testid="credit-entry-save" onClick={addEntry} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Add entry</button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="ledger-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search invoice no / particulars…"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          </div>
          <div><div className="text-[11px] text-muted-foreground mb-0.5">From</div>
            <input data-testid="ledger-page-start" type="date" className={dateField} value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><div className="text-[11px] text-muted-foreground mb-0.5">To</div>
            <input data-testid="ledger-page-end" type="date" className={dateField} value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          {(start || end) && (
            <button data-testid="ledger-page-clear" onClick={() => { setStart(""); setEnd(""); }}
              className="h-9 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
          )}
          {data && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-2 ml-auto">
              <div className="text-xs text-muted-foreground">Outstanding</div>
              <div className="num text-lg font-bold text-destructive">{inr(data.outstanding)}</div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-[100px_150px_minmax(0,1fr)_120px_120px_140px] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Date</span><span>Ref</span><span>Particulars</span><span className="text-right">Debit</span><span className="text-right">Credit</span><span className="text-right">Balance</span>
            </div>
            <div className="divide-y divide-border">
              {rows.map((r, i) => (
                <div key={i} data-testid={`ledger-row-${i}`} className="grid grid-cols-[100px_150px_minmax(0,1fr)_120px_120px_140px] gap-3 px-5 py-2.5 items-center text-sm">
                  <span className="num text-muted-foreground">{r.date}</span>
                  <span className="num text-xs text-primary break-all">{r.ref}</span>
                  <span className="break-words min-w-0">{r.particulars}{r.vehicle ? <span className="ml-1.5 text-[10px] text-muted-foreground num">· Veh: {r.vehicle}</span> : null}{r.manual_id && canWrite ? <button data-testid={`credit-ledger-del-${i}`} onClick={() => delEntry(r.manual_id)} className="ml-2 text-muted-foreground hover:text-destructive transition-colors align-middle"><Trash2 size={12} /></button> : null}</span>
                  <span className="num text-right">{r.debit ? inr(r.debit) : "—"}</span>
                  <span className="num text-right text-[hsl(var(--positive))]">{r.credit ? inr(r.credit) : "—"}</span>
                  <span className="num text-right font-semibold">{fmtBal(r.balance)}</span>
                </div>
              ))}
              {rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No transactions{q ? " match your search" : ""}.</div>}
            </div>
            {data && (
              <div className="grid grid-cols-[100px_150px_minmax(0,1fr)_120px_120px_140px] gap-3 px-5 py-3 bg-muted text-sm font-bold">
                <span></span><span></span><span className="text-right">Closing balance</span>
                <span className="num text-right">{inr(data.debit)}</span>
                <span className="num text-right text-[hsl(var(--positive))]">{inr(data.credit)}</span>
                <span className="num text-right text-destructive">{fmtBal(closing)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
